import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { apiRequest } from "../../utils/api";

const DynamicTextEdit = () => {
  const { area } = useParams();
  const [formData, setFormData] = useState({
    id: null,
    area: area,
    title: area,
    content: "",
    link_url: "",
    status: "active",
    styles: {
      color: "#000000",
      backgroundColor: "#ffffff",
      fontSize: "16px",
      fontWeight: "normal",
      textAlign: "left",
    },
  });

  const [loading, setLoading] = useState(true);

  // ✅ Normalize styles (fix incomplete or invalid values)
  const normalizeStyles = (styles) => {
    const defaults = {
      color: "#000000",
      backgroundColor: "#ffffff",
      fontSize: "16px",
      fontWeight: "normal",
      textAlign: "left",
    };

    let parsed = { ...defaults, ...styles };

    // Fix fontSize if invalid (like "px" or empty)
    if (!parsed.fontSize || !/\d/.test(parsed.fontSize)) {
      parsed.fontSize = "16px";
    }

    return parsed;
  };

  // ✅ Fetch data from API if editing existing area
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await apiRequest.get(`/dynamic-text/texts/${area}`);
        if (res.data.status === 200) {
          const dbData = res.data.message[0];

          let styles = dbData.styles;
          if (typeof styles === "string") {
            try {
              styles = JSON.parse(styles);
            } catch {
              styles = {};
            }
          }

          setFormData((prev) => ({
            ...prev,
            ...dbData,
            styles: normalizeStyles(styles),
          }));
        }
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    if (area && area !== "new") {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [area]);

  // ✅ Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (
      [
        "color",
        "backgroundColor",
        "fontSize",
        "fontWeight",
        "textAlign",
      ].includes(name)
    ) {
      setFormData((prev) => ({
        ...prev,
        styles: { ...prev.styles, [name]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ✅ Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (formData.id) {
        await apiRequest.put(`/dynamic-text/texts/${formData.id}`, formData);
        alert("✅ Text updated successfully!");
      } else {
        const res = await apiRequest.post("/dynamic-text/texts", formData);
        if (res.data.status === 200) {
          alert("✅ Text saved successfully!");
        } else {
          alert(res.data.message || "Failed to save text");
        }
      }
    } catch (err) {
      console.error(err);
      alert("❌ Save failed");
    }
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="container mt-4">
      {area === "new" ? (
        <h3>Add Area</h3>
      ) : (
        <h3 style={{ textTransform: "capitalize" }}>
          Edit Text ({formData.area})
        </h3>
      )}

      <form
        onSubmit={handleSubmit}
        className="p-3 border rounded shadow-sm bg-light"
      >
        {/* Area (only if new) */}
        {area === "new" && (
          <div className="mb-3">
            <label className="form-label">Area</label>
            <input
              type="text"
              name="area"
              className="form-control"
              value={formData.area}
              onChange={handleChange}
              required
            />
          </div>
        )}

        {/* Title */}
        {/* <div className="mb-3">
          <label className="form-label">Title</label>
          <input
            type="text"
            name="title"
            className="form-control"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div> */}

        {/* Content */}
        <div className="mb-3">
          <label className="form-label">Content</label>
          <textarea
            name="content"
            className="form-control"
            rows="3"
            value={formData.content}
            onChange={handleChange}
            required
          />
        </div>

        {/* Link */}
        {/* <div className="mb-3">
          <label className="form-label">Link URL</label>
          <input
            type="text"
            name="link_url"
            className="form-control"
            value={formData.link_url}
            onChange={handleChange}
          />
        </div> */}

        {/* Styles */}
        <h5 className="mt-3">Styles</h5>
        <div className="row">
          {/* <div className="col-md-4 mb-3">
            <label className="form-label">Text Color</label>
            <input
              type="color"
              name="color"
              className="form-control form-control-color"
              value={formData.styles.color}
              onChange={handleChange}
            />
          </div> */}
          {/* <div className="col-md-4 mb-3">
            <label className="form-label">Background</label>
            <input
              type="color"
              name="backgroundColor"
              className="form-control form-control-color"
              value={formData.styles.backgroundColor}
              onChange={handleChange}
            />
          </div> */}
          <div className="col-md-4 mb-3">
            <label className="form-label">Font Size</label>
            <input
              type="text"
              name="fontSize"
              className="form-control"
              value={formData.styles.fontSize}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label">Font Weight</label>
            <select
              name="fontWeight"
              className="form-select"
              value={formData.styles.fontWeight}
              onChange={handleChange}
            >
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
              <option value="lighter">Light</option>
            </select>
          </div>
          {/* <div className="col-md-6 mb-3">
            <label className="form-label">Text Align</label>
            <select
              name="textAlign"
              className="form-select"
              value={formData.styles.textAlign}
              onChange={handleChange}
            >
              <option value="left">Left</option>
              <option value="center">Center</option>
              <option value="right">Right</option>
            </select>
          </div> */}
        </div>

        {/* Status */}
        {/* <div className="mb-3">
          <label className="form-label">Status</label>
          <select
            name="status"
            className="form-select"
            value={formData.status}
            onChange={handleChange}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div> */}

        {/* Preview */}
        <h5 className="mt-3">Preview</h5>
        <div className="p-3 border rounded" style={formData.styles}>
          {/* <h4>{formData.title || "Banner Title"}</h4> */}
          <p style={{ color: "black " }}>
            {formData.content || "Banner content goes here"}
          </p>
          {formData.link_url && (
            <a href={formData.link_url} target="_blank" rel="noreferrer">
              Visit Link
            </a>
          )}
        </div>

        <button type="submit" className="btn btn-primary mt-3">
          {formData.id ? "Update Text" : "Save Text"}
        </button>
      </form>
    </div>
  );
};

export default DynamicTextEdit;
